<div class="dk-modal__header">
  <div>
    <h2 class="dk-modal__title" [id]="titleId">HubSpot settings for "{{ data.projectName }}"</h2>
    <p class="dk-modal__description">Turn this Client's HubSpot tickets into Requests in this project.</p>
  </div>
  <button type="button" class="dk-modal__close" aria-label="Close" [disabled]="busy()" (click)="close()"></button>
</div>

<form class="dk-modal__body hubspot-dialog__body" [formGroup]="form" (ngSubmit)="save()">
  @if (brokenMessage) {
    <div class="dk-alert dk-alert--warning" role="alert">
      <div class="dk-alert__body">
        <p>{{ brokenMessage }}</p>
      </div>
    </div>
  }

  <!-- Gone since the last save; the next save of any field clears them. -->
  @for (notice of removedCompanyNotices; track notice) {
    <div class="dk-alert dk-alert--warning" role="status">
      <div class="dk-alert__body">
        <p>{{ notice }}</p>
      </div>
    </div>
  }

  @if (portalLinkOptions.length > 1) {
    <div class="dk-form-group">
      <label class="dk-form-group__label" for="hubspotAccount">HubSpot account</label>
      <select
        id="hubspotAccount"
        class="dk-field dk-field--select"
        formControlName="portalLinkId"
        [attr.aria-invalid]="fieldErrors().portal_link_id ? 'true' : null"
        [attr.aria-describedby]="
          (isEditing ? 'hubspotAccountHint' : '') + (fieldErrors().portal_link_id ? ' hubspotAccountError' : '') || null
        "
      >
        @for (link of portalLinkOptions; track link.id) {
          <option [value]="link.id">{{ link.label }}</option>
        }
      </select>
      @if (isEditing) {
        <p class="dk-form-group__hint" id="hubspotAccountHint">Remove the connection to switch accounts.</p>
      }
      @if (fieldErrors().portal_link_id; as error) {
        <p class="dk-form-group__error" id="hubspotAccountError">{{ error }}</p>
      }
    </div>
  }

  <div class="dk-form-group">
    <label class="dk-form-group__label" id="hubspotCompanyLabel" for="hubspotCompany">
      Client companies <span class="dk-form-group__optional">Optional</span>
    </label>
    <dk-multi-select
      controlId="hubspotCompany"
      ariaLabelledBy="hubspotCompanyLabel"
      formControlName="companyIds"
      placeholder="Search companies…"
      searchPlaceholder="Search companies"
      emptyText="No companies found"
      [options]="companyOptions()"
      [searchable]="true"
      [loading]="companiesLoading()"
      [invalid]="companyInvalid()"
      [describedBy]="
        'hubspotCompanyHint' +
        (companiesError() ? ' hubspotCompanyLoadError' : '') +
        (fieldErrors().company_ids ? ' hubspotCompanyError' : '')
      "
      (searchChanged)="onCompanySearch($event)"
    />
    <p class="dk-form-group__hint" id="hubspotCompanyHint">
      New tickets come here when their company's domain matches one of this project's sites. List companies to also
      bring in tickets from companies with no matching site, or to settle which project gets them when several share a
      site.
    </p>
    @if (companiesError(); as error) {
      <div class="hubspot-dialog__load-error">
        <p class="dk-form-group__error" id="hubspotCompanyLoadError">{{ error }}</p>
        <button type="button" class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm" (click)="retryCompanies()">
          Retry
        </button>
      </div>
    }
    @if (fieldErrors().company_ids; as error) {
      <p class="dk-form-group__error" id="hubspotCompanyError">{{ error }}</p>
    }
  </div>

  <div class="dk-form-group">
    <label class="dk-form-group__label" id="hubspotPipelineLabel" for="hubspotPipeline">
      Pipelines <span class="dk-form-group__optional">Optional</span>
    </label>
    <dk-multi-select
      controlId="hubspotPipeline"
      ariaLabelledBy="hubspotPipelineLabel"
      formControlName="pipelineIds"
      placeholder="All pipelines"
      [options]="pipelineOptions()"
      [loading]="pipelinesLoading()"
      [invalid]="pipelineInvalid()"
      [describedBy]="
        'hubspotPipelineHint' +
        (pipelinesError() ? ' hubspotPipelineLoadError' : '') +
        (fieldErrors().pipeline_ids ? ' hubspotPipelineError' : '')
      "
    />
    <p class="dk-form-group__hint" id="hubspotPipelineHint">
      Only tickets in these pipelines become Requests. None picked: every pipeline.
    </p>
    @if (pipelinesError(); as error) {
      <div class="hubspot-dialog__load-error">
        <p class="dk-form-group__error" id="hubspotPipelineLoadError">{{ error }}</p>
        <button type="button" class="dk-btn dk-btn--secondary dk-btn--outline dk-btn--sm" (click)="loadPipelines()">
          Retry
        </button>
      </div>
    }
    @if (fieldErrors().pipeline_ids; as error) {
      <p class="dk-form-group__error" id="hubspotPipelineError">{{ error }}</p>
    }
  </div>

  <div class="dk-form-group">
    <label class="dk-form-group__label" for="hubspotRequestType">
      Request type <span class="dk-form-group__required" aria-hidden="true">*</span>
    </label>
    <select
      id="hubspotRequestType"
      class="dk-field dk-field--select"
      formControlName="requestTypeId"
      required
      [attr.aria-invalid]="fieldErrors().default_request_type_id ? 'true' : null"
      [attr.aria-describedby]="
        'hubspotRequestTypeHint' +
        (requestTypesError() ? ' hubspotRequestTypeLoadError' : '') +
        (fieldErrors().default_request_type_id ? ' hubspotRequestTypeError' : '')
      "
    >
      <option value="">Select a type…</option>
      @for (type of requestTypes(); track type.id) {
        <option [value]="type.id">{{ type.name }}</option>
      }
    </select>
    <p class="dk-form-group__hint" id="hubspotRequestTypeHint">Every Request from HubSpot gets this type.</p>
    @if (requestTypesError(); as error) {
      <p class="dk-form-group__error" id="hubspotRequestTypeLoadError">{{ error }}</p>
    }
    @if (fieldErrors().default_request_type_id; as error) {
      <p class="dk-form-group__error" id="hubspotRequestTypeError">{{ error }}</p>
    }
  </div>

  @if (siteHint(); as lines) {
    <div class="dk-alert dk-alert--info" role="status">
      <div class="dk-alert__body">
        @for (line of lines; track line) {
          <p>{{ line }}</p>
        }
        @for (line of companySiteHints(); track line) {
          <p>{{ line }}</p>
        }
      </div>
    </div>
  }

  @if (connection) {
    <div class="hubspot-dialog__since">
      <p class="dk-caption">
        @if (connection.connected_by) {
          Connected by {{ connection.connected_by.name }} on {{ connection.connected_at | date: 'mediumDate' }}.
        } @else {
          Connected on {{ connection.connected_at | date: 'mediumDate' }}.
        }
      </p>
      <p class="dk-caption">Only tickets created after this date become Requests.</p>
    </div>
  }

  @if (saveError(); as error) {
    <div class="dk-alert dk-alert--destructive" role="alert">
      <div class="dk-alert__body">
        <p>{{ error }}</p>
      </div>
    </div>
  }

  @if (data.canDisconnectAccount) {
    <div class="hubspot-dialog__account">
      <button
        type="button"
        class="dk-btn dk-btn--ghost dk-btn--destructive dk-btn--sm"
        [disabled]="busy()"
        (click)="disconnectAccount()"
      >
        Disconnect HubSpot account…
      </button>
    </div>
  }
</form>

<div class="dk-modal__footer" [class.dk-modal__footer--spread]="isEditing">
  @if (isEditing) {
    <button
      type="button"
      class="dk-btn dk-btn--destructive dk-btn--outline"
      [disabled]="busy()"
      [attr.data-loading]="removing() ? '' : null"
      [attr.aria-busy]="removing()"
      (click)="removeFromProject()"
    >
      Remove from project
    </button>
  }
  <div class="hubspot-dialog__footer-actions">
    <button type="button" class="dk-btn dk-btn--secondary" [disabled]="busy()" (click)="close()">Cancel</button>
    <button
      type="button"
      class="dk-btn"
      [disabled]="!canSave()"
      [attr.data-loading]="saving() ? '' : null"
      [attr.aria-busy]="saving()"
      (click)="save()"
    >
      {{ saving() ? 'Saving…' : 'Save' }}
    </button>
  </div>
</div>
